import React from "react";

import { AspectRatio, Container, Stack } from "@mantine/core";
import objectHash from "object-hash";

import {
  Checklist,
  ChecklistContents,
  ChecklistDocumentElement,
  ChecklistElement,
  ChecklistHeader,
  ChecklistItem,
  isChecklistHeader,
  isChecklistItem,
  isPageBreak,
} from "../../../lib/model/checklistAppState";
import { ChecklistDocumentStyles } from "../../../lib/model/documentStyles";
import { DocumentLayout } from "../../../lib/model/layout";

import { Page, Text, View } from "./SoftPDFComponents";

export function partitionPages(
  checklistDocumentElements: ChecklistDocumentElement[]
): Checklist[][] {
  const partitions: Checklist[][] = [[]];

  checklistDocumentElements.forEach((checklistDocumentElement) => {
    if (isPageBreak(checklistDocumentElement)) {
      partitions.push([]);
    } else {
      partitions.at(-1)?.push(checklistDocumentElement);
    }
  });

  return partitions;
}

export const SoftPDFPreview = ({
  checklistDocumentSnapshot,
  stylesheetSnapshot,
  layout,
}: {
  checklistDocumentSnapshot: ChecklistContents;
  stylesheetSnapshot: ChecklistDocumentStyles;
  layout: DocumentLayout;
}): JSX.Element => {
  const checklistDocument = checklistDocumentSnapshot;
  const documentStyles = stylesheetSnapshot;

  const documentPages = partitionPages(checklistDocument.checklists);
  const documentTitle = checklistDocument.title;

  const enumeration = (checklistPage: Checklist[]): JSX.Element => {
    switch (layout) {
      case "twoColumn":
        return (
          <AspectRatio
            ratio={8.5 / 11}
            style={{
              justifyContent: "flex-start !important",
              height: "11in",
            }}
          >
            <View
              style={{
                justifyContent: "flex-start !important",
                justifyItems: "start",
                ...documentStyles.checklistDocumentBody,
              }}
            >
              {checklistPage.map((checklist) => {
                return (
                  <ChecklistPreview
                    key={checklist.id}
                    checklist={checklist}
                    stylesheetSnapshot={documentStyles}
                    layout={layout}
                  />
                );
              })}
            </View>
          </AspectRatio>
        );
      case "oneColumn":
        return (
          <View
            style={{
              justifyContent: "flex-start !important",
              justifyItems: "start",
              ...documentStyles.checklistDocumentBody,
            }}
          >
            {checklistPage.map((checklist) => {
              return (
                <ChecklistPreview
                  key={checklist.id}
                  checklist={checklist}
                  stylesheetSnapshot={documentStyles}
                  layout={layout}
                />
              );
            })}
          </View>
        );
    }
    const _exhaustiveCheck: never = layout;
    return _exhaustiveCheck;
  };

  return (
    <Container style={{ width: "100%" }}>
      <Stack>
        {documentPages.map((checklistPage) => (
          <Page style={documentStyles.checklistDocument} key={objectHash(checklistPage)}>
            <Text style={documentStyles.checklistDocumentTitle}>{documentTitle.content}</Text>
            <Stack justify="flex-start">{enumeration(checklistPage)}</Stack>
          </Page>
        ))}
      </Stack>
    </Container>
  );
};

const ChecklistPreview = ({
  checklist,
  stylesheetSnapshot,
  layout,
}: {
  checklist: Checklist;
  stylesheetSnapshot: ChecklistDocumentStyles;
  layout: DocumentLayout;
}): JSX.Element => {
  return (
    <View
      style={{
        ...stylesheetSnapshot.checklist,
        width: `${layout === "twoColumn" ? "49%" : "100%"}`,
      }}
    >
      {!!checklist.title.content && (
        <Text style={stylesheetSnapshot.checklistTitle}>{checklist.title.content}</Text>
      )}
      <View style={stylesheetSnapshot.checklistBody}>
        {checklist.content.map((checklistElement: ChecklistElement) => {
          if (isChecklistItem(checklistElement)) {
            return (
              <ChecklistItemPreview
                key={objectHash(checklistElement)}
                checklistItem={checklistElement}
                stylesheetSnapshot={stylesheetSnapshot}
              />
            );
          } else if (isChecklistHeader(checklistElement)) {
            return (
              <ChecklistHeaderPreview
                key={objectHash(checklistElement)}
                checklistHeader={checklistElement}
                stylesheetSnapshot={stylesheetSnapshot}
              />
            );
          }
        })}
      </View>
    </View>
  );
};

const ChecklistItemPreview = ({
  checklistItem,
  stylesheetSnapshot,
}: {
  checklistItem: ChecklistItem;
  stylesheetSnapshot: ChecklistDocumentStyles;
}): JSX.Element => {
  const styles = { ...stylesheetSnapshot.checklistItem, ...checklistItem.styles };
  return (
    <View style={styles}>
      <Text style={stylesheetSnapshot.checklistItemLabel}>{checklistItem.item.content}</Text>
      <Text style={{ ...stylesheetSnapshot.checklistItemSeparatorText, height: "1em" }}>
        {".".repeat(1000)}
      </Text>
      <Text style={stylesheetSnapshot.checklistItemSetting}>{checklistItem.setting.content}</Text>
    </View>
  );
};

const ChecklistHeaderPreview = ({
  checklistHeader,
  stylesheetSnapshot,
}: {
  checklistHeader: ChecklistHeader;
  stylesheetSnapshot: ChecklistDocumentStyles;
}): JSX.Element => {
  const styles = { ...stylesheetSnapshot.checklistHeader, ...checklistHeader.styles };
  return <Text style={styles}>{checklistHeader.text.content}</Text>;
};
